'use client';
import { useMemo } from 'react';

import { ProcessingMintRequest } from '@flipster/apis/v1/earn/types';
import { ProcessingMintRequestsContents } from '@flipster/features/src/flipster-earn/mint/processing-mint-requests-contents';
import { FormattedMessage } from '@flipster/i18n';
import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { IconButton } from '@flipster/react/design-system/icon-button';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, square } from '@flipster/styled-system/patterns';

export const ProcessingMintRequests = ({ requests }: { requests: ProcessingMintRequest[] }) => {
  const maxEstProcessingTimeInMins = useMemo(
    () => Math.max(...(requests?.map((mint) => mint.estProcessingTimeInMins) ?? [0])),
    [requests],
  );
  const tooltipContents = (
    <ul className={css({ list: 'disc' })}>
      <li>
        <FormattedMessage
          id='Autogen_common.r251126.s_Minting_and_redemption_take_about_maxEstProcessingTimeInMins_minutes_depending_on_network_conditions'
          values={{
            maxEstProcessingTimeInMins: maxEstProcessingTimeInMins,
          }}
        />
      </li>
      <li>
        <FormattedMessage id='Autogen_common.r251126.s_The_requested_amount_is_being_processed_and_cannot_be_used_until_completed' />
      </li>
    </ul>
  );

  return (
    <details className={css({ borderTopWidth: '1px', borderTopStyle: 'solid', borderColor: 'border.default.level-1' })}>
      <summary
        className={hstack({
          h: 48,
          cursor: 'pointer',
          userSelect: 'none',
          justify: 'space-between',
        })}
      >
        <div className={hstack()}>
          <span
            className={css({
              textStyle: 'Text/M/Regular',
              color: 'content.default.level-5',
            })}
          >
            <FormattedMessage id='Autogen_common.r251126.s_Processing_requests' />
          </span>
          <Tooltip
            placement='bottom'
            crossAxisOffset={75}
            maxWidth={500}
            mobileAlert={() => {
              dialog.open({
                title: {
                  label: 'Processing requests',
                },
                content: tooltipContents,
                buttons: {
                  primary: <Dialog.Button hierarchy='primary' label='OK' />,
                },
              });
            }}
          >
            <Tooltip.Trigger asChild>
              <IconButton icon={<InformationCircleIcon />} size='small' hierarchy='quaternary' />
            </Tooltip.Trigger>
            <Tooltip.Content>{tooltipContents}</Tooltip.Content>
          </Tooltip>
        </div>
        <span
          className={square({
            size: 24,
            color: 'content.default.level-3',
            '[open] &': { transform: 'rotate(-180deg)' },
          })}
        >
          <ChevronDownIcon size={16} />
        </span>
      </summary>
      <ProcessingMintRequestsContents processingMintRequests={requests} />
    </details>
  );
};
